<template>
  <span ref="root" class="inline-flex" @focusout="onFocusOut">
    <!-- Not a <button>: a disabled fieldset, as viewers get, would disable it too. -->
    <span
      ref="trigger"
      role="button"
      tabindex="0"
      :aria-label="`About ${name}`"
      :aria-expanded="open"
      :aria-controls="`${id}-popover`"
      :title="short"
      class="inline-flex cursor-pointer rounded-full text-gray-400 hover:text-gray-600 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600 dark:text-gray-500 dark:hover:text-gray-300"
      :data-testid="`${id}-info`"
      @click="open = !open"
      @keydown.enter.prevent="open = !open"
      @keydown.space.prevent="open = !open"
    >
      <InformationCircleIcon class="size-5" aria-hidden="true" />
    </span>
    <span :id="`${id}-help`" class="sr-only">{{ short }}</span>
    <div
      v-show="open"
      :id="`${id}-popover`"
      tabindex="-1"
      class="absolute inset-x-0 top-full z-20 mt-1 rounded-md outline-none bg-white p-3 text-sm shadow-lg ring-1 ring-gray-900/10 dark:bg-gray-800 dark:ring-white/10"
      :data-testid="`${id}-popover`"
    >
      <p class="font-medium text-gray-900 dark:text-white">{{ short }}</p>
      <p class="mt-2 text-gray-600 dark:text-gray-300">{{ long }}</p>
    </div>
  </span>
</template>

<script setup lang="ts">
import { onBeforeUnmount, ref, watch } from 'vue';
import { InformationCircleIcon } from '@heroicons/vue/20/solid';

/**
 * The popover spans the nearest positioned ancestor and opens below it, so the field it explains stays visible.
 * `${id}-help` holds `short` for `aria-describedby`.
 */
defineProps<{ id: string; name: string; short: string; long: string }>();

const open = ref(false);
const root = ref<HTMLElement | null>(null);
const trigger = ref<HTMLElement | null>(null);

function onPointerDown(event: PointerEvent) {
  if (!root.value?.contains(event.target as Node)) open.value = false;
}

// The popover takes focus when clicked, so reading or selecting its text keeps it open.
function onFocusOut(event: FocusEvent) {
  if (!root.value?.contains(event.relatedTarget as Node | null)) open.value = false;
}

function onKeyDown(event: KeyboardEvent) {
  if (event.key !== 'Escape') return;
  if (root.value?.contains(document.activeElement)) trigger.value?.focus();
  open.value = false;
}

function stopListening() {
  document.removeEventListener('pointerdown', onPointerDown);
  document.removeEventListener('keydown', onKeyDown);
}

watch(open, (isOpen) => {
  if (!isOpen) return stopListening();
  document.addEventListener('pointerdown', onPointerDown);
  document.addEventListener('keydown', onKeyDown);
});
onBeforeUnmount(stopListening);
</script>
